import { Button, FileUploadPattern, FormGroup, Heading, Input, Modal } from '@snsw/react-component-library';
import { PlantDesignDrawingAndTechDetailsPage } from 'plantDesign/constants/plantDesignDrawingAndTechDetailsPage';
import { removeDrawingFile } from 'plantDesign/redux/plantDesignDrawingAndTechSlice';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getAttachmentApi } from 'shared/utils/Helpers';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from '../DrawingsAndTechDetails.module.scss';

export const DrawingDetails = React.forwardRef((props, ref) => {
  const {
    index = 0,
    details = {},
    onChange = () => {},
    onErrorChange = () => {},
    showRemoveButton = false,
    onRemoveClick = () => {},
    triggerValidate = false,
    applicationId,
  } = props;
  const {
    title: drawingTitle = '',
    number: drawingNumber = '',
    revisionNumber: drawingRevisionNumber = '',
    uploadedDocument: drawingFile = [],
  } = details;

  const dispatch = useDispatch();

  const [title, setTitle] = useState(drawingTitle);
  const [number, setNumber] = useState(drawingNumber);
  const [revisionNumber, setRevisionNumber] = useState(drawingRevisionNumber);
  const [uploadedDocument, setUploadedDocument] = useState(drawingFile);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const attachmentApi = getAttachmentApi(applicationId);

  const PAGE_CONSTANTS = PlantDesignDrawingAndTechDetailsPage.addDrawing;

  const validators = [
    {
      fieldName: `${index}_title`,
      fieldValue: title,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${index}_number`,
      fieldValue: number,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${index}_uploadedDocument`,
      fieldValue: uploadedDocument,
      isValid: (v) => v && v.length > 0,
      errorMessage: () => 'Please upload a representational drawing',
    },
  ];

  const validate = () => {
    const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    setShowErrors(!isCurrentComponentValid);

    return isCurrentComponentValid;
  };

  const removeDetails = () => {
    setShowModal(true);
  };

  const cancelRemove = () => {
    setShowModal(false);
  };

  const confirmRemove = () => {
    setShowModal(false);
    onRemoveClick(index);
  };

  const onRemovedFile = (value) => {
    setUploadedDocument(value);
    dispatch(removeDrawingFile(index));
  };

  useImperativeHandle(ref, () => ({
    validate: () => validate(),
  }));

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  useEffect(() => {
    if (showErrors) validate();

    onChange({ title, number, revisionNumber, uploadedDocument });
  }, [title, number, revisionNumber, uploadedDocument]);

  useEffect(() => {
    if (triggerValidate) {
      validate();
    }
  }, [triggerValidate]);

  return (
    <>
      <Heading level={3} className={styles.drawingTitle}>
        {(index + 1).toString().padStart(2, '0')} {PAGE_CONSTANTS.drawingDetailsTitle}
      </Heading>
      <span className={styles.helpMsg}>{PAGE_CONSTANTS.drawingDetailsHelpMessage}</span>
      <FormGroup
        id={index + '_title'}
        label={PAGE_CONSTANTS.titleLabel}
        isRequired
        errorMessage={errors[`${index}_title`]?.errorMessage}
        hasError={showErrors && errors[`${index}_title`]?.invalid}
      >
        <Input
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          value={title}
        />
      </FormGroup>
      <FormGroup
        id={index + '_number'}
        label={PAGE_CONSTANTS.numberLabel}
        isRequired
        errorMessage={errors[`${index}_number`]?.errorMessage}
        hasError={showErrors && errors[`${index}_number`]?.invalid}
      >
        <Input
          onChange={(e) => {
            setNumber(e.target.value);
          }}
          value={number}
          inputWidth="xl"
        />
      </FormGroup>
      <FormGroup
        id={index + '_revisionNumber'}
        label={PAGE_CONSTANTS.revisionLabel}
        helpMessage={PAGE_CONSTANTS.revisionHelpMessage}
        isRequired
        errorMessage={errors[`${index}_revisionNumber`]?.errorMessage}
        hasError={showErrors && errors[`${index}_revisionNumber`]?.invalid}
      >
        <Input
          onChange={(e) => {
            setRevisionNumber(e.target.value);
          }}
          value={revisionNumber}
          inputWidth="xl"
        />
      </FormGroup>
      {uploadedDocument && uploadedDocument.length == 0 && (
        <>
          <Heading level={4} style={{ marginBottom: 0 }}>
            Requirements for uploading a drawing:
          </Heading>
          <ol className={styles.listItem}>
            <li>Documents must be in English.</li>
            <li>
              If you are uploading a scanned copy of a print document, check the image is clear and all text is legible.
            </li>
            <li>Upload one file for each document. This includes scanning a multi-page document into a single file.</li>
          </ol>
        </>
      )}
      {uploadedDocument && uploadedDocument.length > 0 && (
        <Heading level={4} style={{ marginBottom: '1rem', marginTop: '2rem' }}>
          Uploaded representational drawing:
        </Heading>
      )}
      <div
        id={`${index}_uploadedDocument`}
        className={uploadedDocument && uploadedDocument.length > 0 ? styles.uploadedFile : styles.uploadFile}
      >
        <FileUploadPattern
          api={attachmentApi}
          name={index + '_uploadFile'}
          id={index + '_uploadFile'}
          legendText=""
          initialFiles={uploadedDocument}
          onUploaded={setUploadedDocument}
          onRemoved={onRemovedFile}
          maxMBSize={10}
          acceptedFormats=".png,.jpg,.pdf"
          errorMessage={errors[`${index}_uploadedDocument`]?.errorMessage}
        />
      </div>
      {showRemoveButton && (
        <Button className={styles.marginTopOne} variant="link" onClick={removeDetails}>
          Remove all details for this drawing
        </Button>
      )}
      {showModal && (
        <Modal
          title="Are you sure you want to remove this drawing?"
          description="This will delete all details for the drawing."
          buttons={[
            { text: 'Remove', onClick: confirmRemove },
            { text: 'Cancel', onClick: cancelRemove },
          ]}
        />
      )}
    </>
  );
});

DrawingDetails.displayName = 'DrawingDetails';
